import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { getValidToken } from "../../utils/tokenManager";
import Button from "../common/Button";
import logoIcon from "../../assets/common/logo-icon.svg";
import logout from "../../assets/common/logout.svg";
import editIcon from "../../assets/dashboard/edit-icon.svg";
import success from "../../assets/agent/success.svg";
import fail from "../../assets/agent/fail.svg";
import AdminSidebar from "components/common/AdminSidebar";
import MobileScheduledMeetings from "./mobile/MobileScheduledMeetings";
import PageSizeFilter from "./adminComponents/PageSizeFilter";
import TableSearch from "./adminComponents/TableSearch";
import TableSortDropdown from "./adminComponents/TableSortDropdown";

const Client = () => {
  const { t } = useTranslation();
  const user = sessionStorage.getItem("userName");
  const [clientName, setClientName] = useState(
    sessionStorage.getItem("clientName") || ""
  );

  const [editingIdx, setEditingIdx] = useState(null);
  const [selectedAttended, setSelectedAttended] = useState("");
  const [agentsState, setAgentsState] = useState([]);
  const [successModal, setSuccessModal] = useState(false);
  const [errorModal, setErrorModal] = useState(false);
  const [errors, setErrors] = useState({});
  const editRef = useRef(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [sidebarPinned, setSidebarPinned] = useState(() => {
    const saved = localStorage.getItem("adminSidebarPinned");
    return saved === "true";
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [searchField, setSearchField] = useState("name");
  const [sortConfig, setSortConfig] = useState({
    field: "originalDate",
    direction: "desc", // default newest first
  });

  const fields = [
    { label: t("confirm.table.name"), value: "name" },
    { label: t("dashboard.table.company"), value: "company" },
    { label: t("email"), value: "mail" },
  ];

  const fetchMeetings = async () => {
    try {
      const accessToken = await getValidToken();
      const response = await axios.get(
        `${process.env.REACT_APP_APIBASEURL}/api/meetings/all`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      const mappedData = response.data.meetingLogs.map((meeting) => {
        const meetingDate = moment.utc(meeting.Date).tz("America/Bogota");

        return {
          contactId: meeting.ContactId,
          meetingLogId: meeting.Id,
          originalDate: meeting.Date,
          // Correctly format date and time to Colombia time zone
          date: meetingDate.format("MMM DD"),
          time: meetingDate.format("h:mmA"),
          name: meeting.Name,
          lastName: meeting.Lastname,
          company: meeting.Company,
          mail: meeting.Email,
          phone: meeting.Phone,
          attended:
            meeting.Attend === true
              ? "Yes"
              : meeting.Attend === false
                ? "No"
                : "",
          attempt: meeting.Num_intent || 0,
        };
      });

      setAgentsState(mappedData);
    } catch (error) {
      console.error("Error fetching meetings:", error);
    }
  };

  useEffect(() => {
    fetchMeetings();
  }, []);

  useEffect(() => {
    const handleClientAssigned = () => {
      const updatedName = sessionStorage.getItem("clientName") || "";
      setClientName(updatedName);
    };

    window.addEventListener("client-assigned", handleClientAssigned);
    window.addEventListener("storage", handleClientAssigned);

    return () => {
      window.removeEventListener("client-assigned", handleClientAssigned);
      window.removeEventListener("storage", handleClientAssigned);
    };
  }, []);

  const handleClient = (idx) => {
    setEditingIdx(idx);
    setSelectedAttended(agentsState[idx].attended);
  };

  useEffect(() => {
    if (successModal) {
      setTimeout(() => {
        setSuccessModal(false);
      }, 2000);
    }

    const handleClickOutside = (event) => {
      if (
        editRef.current &&
        !editRef.current.contains(event.target) &&
        !event.target.closest("#save-button")
      ) {
        setEditingIdx(null);
        setSelectedAttended("");
      }
    };

    if (editingIdx !== null) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [editingIdx, successModal]);

  const handleCheckboxChange = (value) => {
    setSelectedAttended(value);
  };

  const handleSave = async () => {
    console.log("clicked");
    const contact = agentsState[editingIdx];

    const meeting_log_id = contact.meetingLogId;

    try {
      const accessToken = await getValidToken();
      await axios.put(
        `${process.env.REACT_APP_APIBASEURL}/api/meetings/log/${meeting_log_id}`,
        {
          Attend: selectedAttended === "Yes" ? true : false,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      const updatedAgents = agentsState.map((agent, idx) =>
        idx === editingIdx ? { ...agent, attended: selectedAttended } : agent
      );

      setAgentsState(updatedAgents);
      setEditingIdx(null);
      setSelectedAttended("");
      setSuccessModal(true);
    } catch (error) {
      const errorMessage = error.response?.data?.error || t("error.generic");
      setErrorModal(true);
      setErrors({ submit: errorMessage });
    }
  };

  const handleModal = () => {
    setErrorModal(false);
  };

  const highlight = (text) => {
    if (!searchTerm) return text;

    const regex = new RegExp(`(${searchTerm})`, "gi");
    const parts = text.split(regex);

    return parts.map((part, i) =>
      part.toLowerCase() === searchTerm.toLowerCase() ? (
        <span key={i} className="bg-yellow-200 font-semibold">
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  const filteredData = agentsState.filter((item) => {
    if (!searchTerm.trim()) return true;

    const value = item[searchField];
    if (!value) return false;

    return value
      .toString()
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
  });

  // SORT
  const sortedData = [...filteredData].sort((a, b) => {
    const { field, direction } = sortConfig;

    if (!field || !direction) return 0;

    let valueA = a[field];
    let valueB = b[field];

    // Date sorting
    if (field === "originalDate") {
      valueA = new Date(a.originalDate);
      valueB = new Date(b.originalDate);
      return direction === "asc"
        ? valueA - valueB
        : valueB - valueA;
    }

    // Time sorting (based on originalDate time)
    if (field === "time") {
      const dateA = new Date(a.originalDate);
      const dateB = new Date(b.originalDate);

      const minutesA = dateA.getHours() * 60 + dateA.getMinutes();
      const minutesB = dateB.getHours() * 60 + dateB.getMinutes();

      return direction === "asc"
        ? minutesA - minutesB
        : minutesB - minutesA;
    }

    // Attended sorting (Yes / No)
    if (field === "attended") {
      const valueA = a.attended === "Yes" ? 1 : 0;
      const valueB = b.attended === "Yes" ? 1 : 0;

      return direction === "asc"
        ? valueA - valueB
        : valueB - valueA;
    }

    // String sorting (name, lastName, company, mail)
    valueA = (valueA || "").toString().toLowerCase();
    valueB = (valueB || "").toString().toLowerCase();

    return direction === "asc"
      ? valueA.localeCompare(valueB)
      : valueB.localeCompare(valueA);
  });

  const totalPages = Math.ceil(sortedData.length / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = sortedData.slice(startIndex, startIndex + itemsPerPage);

  return (
    <>
    <div className="hidden lg:flex min-h-screen bg-secondaryBg">
      <AdminSidebar
        logoIcon={logoIcon}
        logout={logout}
        labelClassName="bg-primary"
        onToggle={(pinned) => setSidebarPinned(pinned)}
      />
      <div className={`flex-1 flex flex-col px-4 sm:px-8 py-6 md:py-7 mt-10 transition-all duration-300 ${sidebarPinned ? "ml-[250px]" : "ml-[80px]"
        }`}>
        <div className="max-w-6xl mx-auto w-full">
          <span className="text-lg md:text-xl text-[#0A1E4C] font-medium">
            {t("dashboard.greeting", { name: user })}
          </span>
          <h1 className="text-[30px] font-bold text-primary mb-2">
            {t("welcome")} Kikonnect
          </h1>
          <p className="mb-2 md:text-xl text-primary">
            {t("admin.overview")} {clientName}
          </p>
          <div className="relative flex items-center mb-8">
            <span className="absolute left-0 w-1 h-1 bg-primary rounded-full -ml-1"></span>
            <hr className="flex-1 border-t-2 border-primary" />
            <span className="absolute right-0 w-1 h-1 bg-primary rounded-full -mr-1"></span>
          </div>

          <div className="relative z-50 flex justify-end items-center gap-4">
            <TableSearch
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              searchField={searchField}
              setSearchField={setSearchField}
              fields={fields}
              onSearchChange={() => setCurrentPage(1)}
            />

            <PageSizeFilter
              value={itemsPerPage}
              onChange={(size) => {
                setItemsPerPage(size);
                setCurrentPage(1);
              }}
            />
          </div>

          <div className="mt-5">
            <div className="bg-white rounded-xl shadow-lg p-0 w-[270px] md:w-auto h-auto border border-[#E3E8F0] overflow-x-auto">
              <table className="w-full text-primary">
                <thead>
                  <tr className="bg-primary text-white">
                    <th className="py-3 px-2 sm:px-3 w-[90px]">
                      <TableSortDropdown
                        label={t("admin.option.date")}
                        value={sortConfig.field === "originalDate" ? sortConfig.direction : null}
                        onChange={(value) => {
                          setSortConfig({
                            field: "originalDate",
                            direction: value === "newest" ? "desc" : "asc",
                          });
                          setCurrentPage(1);
                        }}
                        options={[
                          { label: "Newest activity", value: "newest" },
                          { label: "Oldest activity", value: "oldest" },
                        ]}
                      />
                    </th>
                    <th className="py-3 px-2 sm:px-3 w-[80px]">
                      <TableSortDropdown
                        label={t("admin.option.time")}
                        value={sortConfig.field === "time" ? sortConfig.direction : null}
                        onChange={(value) => {
                          setSortConfig({
                            field: "time",
                            direction: value,
                          });
                          setCurrentPage(1);
                        }}
                        options={[
                          { label: "Earliest time", value: "asc" },
                          { label: "Latest time", value: "desc" },
                        ]}
                      />
                    </th>
                    <th className="py-3 px-2 sm:px-3 w-[120px]">
                      <TableSortDropdown
                        label={t("confirm.table.name")}
                        value={sortConfig.field === "name" ? sortConfig.direction : null}
                        onChange={(value) => {
                          setSortConfig({
                            field: "name",
                            direction: value,
                          });
                          setCurrentPage(1);
                        }}
                        options={[
                          { label: "A-Z", value: "asc" },
                          { label: "Z-A", value: "desc" },
                        ]}
                      />
                    </th>
                    <th className="py-3 px-2 sm:px-3 w-[120px]">
                      <TableSortDropdown
                        label={t("confirm.table.lastName")}
                        value={sortConfig.field === "lastName" ? sortConfig.direction : null}
                        onChange={(value) => {
                          setSortConfig({
                            field: "lastName",
                            direction: value,
                          });
                          setCurrentPage(1);
                        }}
                        options={[
                          { label: "A-Z", value: "asc" },
                          { label: "Z-A", value: "desc" },
                        ]}
                      />
                    </th>
                    <th className="py-3 px-2 sm:px-3 w-[200px] break-words">
                      <TableSortDropdown
                        label={t("dashboard.table.company")}
                        value={sortConfig.field === "company" ? sortConfig.direction : null}
                        onChange={(value) => {
                          setSortConfig({
                            field: "company",
                            direction: value,
                          });
                          setCurrentPage(1);
                        }}
                        options={[
                          { label: "A-Z", value: "asc" },
                          { label: "Z-A", value: "desc" },
                        ]}
                      />
                    </th>
                    <th className="py-3 px-2 sm:px-3 w-[220px] break-words">
                      <TableSortDropdown
                        label={t("email")}
                        value={sortConfig.field === "mail" ? sortConfig.direction : null}
                        onChange={(value) => {
                          setSortConfig({
                            field: "mail",
                            direction: value,
                          });
                          setCurrentPage(1);
                        }}
                        options={[
                          { label: "A-Z", value: "asc" },
                          { label: "Z-A", value: "desc" },
                        ]}
                      />
                    </th>
                    <th className="py-3 px-2 sm:px-3 w-[150px]">
                      {t("admin.phone")}
                    </th>
                    <th className="py-3 px-2 sm:px-3 w-[100px]">
                      <TableSortDropdown
                        label={t("admin.option.attended")}
                        value={sortConfig.field === "attended" ? sortConfig.direction : null}
                        onChange={(value) => {
                          setSortConfig({
                            field: "attended",
                            direction: value,
                          });
                          setCurrentPage(1);
                        }}
                        options={[
                          { label: "Yes", value: "desc" },
                          { label: "No", value: "asc" },
                        ]}
                      />
                    </th>
                    <th className="py-3 px-2 sm:px-3 w-[80px]">
                      {t("admin.option.attempt")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((agent, idx) => (
                    <tr
                      key={startIndex + idx}
                      className="text-center border-b last:border-b-0"
                    >
                      <td className="py-3 px-2 whitespace-nowrap">
                        {agent.date}
                      </td>
                      <td className="py-3 px-2 whitespace-nowrap">
                        {agent.time}
                      </td>
                      <td className="py-3 px-2 break-words">{highlight(agent.name)}</td>
                      <td className="py-3 px-2 break-words">
                        {agent.lastName}
                      </td>
                      <td className="py-3 px-2 break-words">{highlight(agent.company)}</td>
                      <td className="py-3 px-2 break-words">{highlight(agent.mail)}</td>
                      <td className="py-3 px-2 whitespace-nowrap">
                        {agent.phone}
                      </td>

                      <td className="py-3 px-2 whitespace-nowrap bg-primary text-white">
                        <div className="flex items-center justify-center gap-2 relative">
                          {editingIdx === startIndex + idx ? (
                            <div
                              ref={editRef}
                              className="flex gap-2 items-center"
                            >
                              <div className="flex gap-2 items-center">
                                <label className="flex items-center gap-1">
                                  <input
                                    type="checkbox"
                                    checked={selectedAttended === "Yes"}
                                    onChange={() => handleCheckboxChange("Yes")}
                                  />
                                  Yes
                                </label>
                                <label className="flex items-center gap-1">
                                  <input
                                    type="checkbox"
                                    checked={selectedAttended === "No"}
                                    onChange={() => handleCheckboxChange("No")}
                                  />
                                  No
                                </label>
                              </div>
                            </div>
                          ) : (
                            <>
                              {agent.attended}
                              <span className="absolute right-0 top-1 w-4 h-4 flex items-center justify-center rounded-full bg-lightBlue cursor-pointer">
                                <img
                                  src={editIcon}
                                  alt="editIcon"
                                  className="w-2 h-2"
                                  onClick={() => handleClient(startIndex + idx)}
                                />
                              </span>
                            </>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-2 whitespace-nowrap">
                        {agent.attempt}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex items-center justify-center space-x-5 text-sm my-2">
            <button
              className={`p-2 rounded border-tone ${currentPage === 1
                ? "bg-gray-100 text-gray-400"
                : "bg-white text-icon"
                }`}
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="2"
                stroke="currentColor"
                className="w-4 h-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <span className="text-[#000] font-semibold">
              <strong className="bg-white p-1 rounded border-tone">
                {currentPage}
              </strong>{" "}
              of {totalPages}
            </span>
            <button
              className={`p-2 rounded border-tone ${currentPage === totalPages
                ? "bg-gray-100 text-gray-400"
                : "bg-white text-icon"
                }`}
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="2"
                stroke="currentColor"
                className="w-4 h-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>

          {editingIdx !== null && (
            <div className="flex justify-center mt-5">
              <Button
                id="save-button"
                className="bg-green-50 text-white font-bold w-[200px]"
                onClick={handleSave}
              >
                {t("button.save")}
              </Button>
            </div>
          )}
        </div>
      </div>

      {successModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#000] bg-opacity-30 px-4">
          <div className="bg-white rounded-lg text-primary text-xl shadow-xl w-full max-w-[660px] p-6 relative flex flex-col items-center">
            <img
              src={success}
              alt="success"
              className="w-[60px] h-[60px] mb-4"
            />
            <p className="mb-4 text-xl font-bold">
              {t("dashboard.attendSuccessMod")}
            </p>
          </div>
        </div>
      )}

      {errorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#000] bg-opacity-30 px-4">
          <div className="bg-white rounded-lg text-primary text-xl shadow-xl w-full max-w-[660px] p-6 relative flex flex-col items-center">
            <img src={fail} alt="fail" className="w-[60px] h-[60px] mb-4" />
            <p className="mb-4 text-xl font-bold">
              {t("admin.userError.head")}
            </p>
            <div className="relative flex items-center mb-8">
              <span className="absolute left-0 w-1 h-1 bg-[#0A1E4C] rounded-full -ml-1"></span>
              <hr className="flex-1 border-t-2 border-[#0A1E4C] w-[350px]" />
              <span className="absolute right-0 w-1 h-1 bg-[#0A1E4C] rounded-full -mr-1"></span>
            </div>
            {errors.submit && (
              <p className="mb-6 text-xl w-[455px] text-center">
                {errors.submit}
              </p>
            )}
            <Button variant="secondary" onClick={handleModal}>
              {t("admin.userError.btn")}
            </Button>
          </div>
        </div>
      )}
    </div>
    <div className="lg:hidden">
      <MobileScheduledMeetings />
    </div>
    </>
  );
};

export default Client;
